import { useEffect, useState } from 'react';
import { useUser } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';

export interface UserAward {
  id: string;
  user_id: string;
  title: string;
  issuer?: string;
  date?: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export const useUserAwards = (userId?: string) => {
  const { user } = useUser();
  const [awards, setAwards] = useState<UserAward[]>([]);
  const [loading, setLoading] = useState(true);

  const targetUserId = userId || user?.id;

  const fetchAwards = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_awards')
        .select('*')
        .eq('user_id', targetUserId as any)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching awards:', error);
        return;
      }

      setAwards((data as any) || []);
    } catch (error) {
      console.error('Error fetching awards:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!targetUserId) {
      setAwards([]);
      setLoading(false);
      return;
    }

    fetchAwards();
    
    // Listen for profile data updates
    const handleProfileUpdate = () => {
      if (targetUserId) {
        fetchAwards();
      }
    };
    
    window.addEventListener('profileDataUpdated', handleProfileUpdate);
    
    return () => {
      window.removeEventListener('profileDataUpdated', handleProfileUpdate);
    };
  }, [targetUserId]);

  const addAward = async (award: Omit<UserAward, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('user_awards')
        .insert({ ...award, user_id: user.id } as any)
        .select()
        .single();

      if (error) throw error;

      setAwards(prev => [(data as any), ...prev]);
      return data;
    } catch (error) {
      console.error('Error adding award:', error);
      throw error;
    }
  };

  const updateAward = async (id: string, updates: Partial<UserAward>) => {
    try {
      const { data, error } = await supabase
        .from('user_awards')
        .update(updates as any)
        .eq('id', id as any)
        .select()
        .single();

      if (error) throw error;

      setAwards(prev => prev.map(a => a.id === id ? (data as any) : a));
      return data;
    } catch (error) {
      console.error('Error updating award:', error);
      throw error;
    }
  };

  const deleteAward = async (id: string) => {
    try {
      const { error } = await supabase
        .from('user_awards')
        .delete()
        .eq('id', id as any);

      if (error) throw error;

      setAwards(prev => prev.filter(a => a.id !== id));
    } catch (error) {
      console.error('Error deleting award:', error);
      throw error;
    }
  };

  return {
    awards,
    loading,
    addAward,
    updateAward,
    deleteAward,
    refetchAwards: fetchAwards
  };
};